import { useEffect, useRef } from "react";
import { useDebounce } from "use-debounce";

import { useAuthStore } from "@/features/auth";
import { useSocket } from "@/lib/socket";
import { useCompanyStore } from "@/features/company";
import { useOrganizationStore } from "@/features/organization";
import { useRolesPermissionsStore } from "./useRolesPermissionsStore";
import { useUserStore } from "./useUserStore";

export const useUserListPageEffects = () => {
  const { user, accessToken } = useAuthStore();
  const currentUserId = user?.userId || user?.id;
  const companyId = user?.companyId;
  const socket = useSocket(currentUserId, accessToken);

  const {
    roles,
    permissionsModules,
    loadingModules,
    loadingRoles,
    fetchRoleList,
    fetchPermissionModules,
  } = useRolesPermissionsStore();
  const { fetchPublicCompanies, companiesLoaded, companyOptions } =
    useCompanyStore();
  const { fetchHierarchy } = useOrganizationStore();
  const orgLoadedRef = useRef(false);
  const {
    page,
    pageSize,
    filters,
    sort,
    checkCanCreateUser,
    updateOnlineUserStatus,
    loadUsers,
  } = useUserStore();

  const [debouncedUsername] = useDebounce(filters.username, 400);
  const [debouncedEmail] = useDebounce(filters.email, 400);
  const [debouncedCompany] = useDebounce(filters.company, 400);

  useEffect(() => {
    if (!companyId) return;
    checkCanCreateUser(companyId);
  }, [checkCanCreateUser, companyId]);

  useEffect(() => {
    if (!roles.length && !loadingRoles) {
      fetchRoleList();
    }
  }, [fetchRoleList, loadingRoles, roles.length]);

  useEffect(() => {
    if (!permissionsModules.length && !loadingModules) {
      fetchPermissionModules({ audience: "all" });
    }
  }, [fetchPermissionModules, loadingModules, permissionsModules.length]);

  useEffect(() => {
    if (orgLoadedRef.current) return;
    orgLoadedRef.current = true;
    fetchHierarchy().catch(() => {});
  }, [fetchHierarchy]);

  useEffect(() => {
    if (companiesLoaded || companyOptions.length > 0) return;
    fetchPublicCompanies().catch(() => {});
  }, [companiesLoaded, companyOptions.length, fetchPublicCompanies]);

  useEffect(() => {
    if (!socket?.on) return;

    const handleOnlineUserStatus = ({ userId, status, lastActive }) => {
      updateOnlineUserStatus(userId, status, lastActive);
    };

    socket.on("user-status", handleOnlineUserStatus);

    return () => {
      socket.off?.("user-status", handleOnlineUserStatus);
    };
  }, [socket, updateOnlineUserStatus]);

  useEffect(() => {
    if (!user) return;
    loadUsers();
  }, [
    user,
    debouncedUsername,
    debouncedEmail,
    debouncedCompany,
    sort.key,
    sort.direction,
    page,
    pageSize,
    loadUsers,
  ]);

  return null;
};
